import classNames from "classnames";
import React from "react";

type AvailableInputTypes =
  | "date"
  | "datetime-local"
  | "email"
  | "number"
  | "password"
  | "search"
  | "tel"
  | "text"
  | "time"
  | "url";
interface BaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Accepts most valid html input types */
  type: AvailableInputTypes;
  /** Name is used in the input elements id and labels elements for values to create a unique
   * label + input pair. If the name is not unique it will cause accessibility issues.
   * Usually you want to name the input after the data it will contain e.g mobile-number,
   * first-name, preferred-name etc. */
  name: string;
  /** Use "large" for the sign up form and marketing site
   * and the default size for everything else. */
  inputSize?: "default" | "large";
  /** The default width is set to match the inputs size to its content.
   * Full width sets the input to have a width of 100% of its parent element. */
  inputWidth?: "default" | "full";
  /** Used to match the background of the current page. Not applicable on "large" size inputs. */
  labelBackgroundColor?: "white" | "linen";
  /** Used to left or right align all text on an input. */
  alignment?: "left" | "right";
  /** Toggle between default/active, disabled, and error states for an input. */
  state?: "default" | "disabled" | "error";
  /** Standard HTML input required attribute. When not present or false `(optional)` text will display in the inputs label. */
  required?: boolean;
  /** The text for the inputs label */
  labelText: string;
  /** Optional placeholder text for an input */
  placeholderText?: string;
  /** Optional helper text for an input. Can also be used to render error messages
   * and will change color to reflect this when used in combination with the error state. */
  helperText?: string;
  /** Visually the same as helper text but rendered separately to allow for both to be
   * present at the same time. */
  errorText?: string;
}

export const BaseInput: React.FC<BaseInputProps> = ({
  type = "text",
  name,
  inputSize,
  inputWidth,
  labelBackgroundColor,
  alignment,
  state,
  placeholderText,
  labelText,
  helperText,
  errorText,
  ...props
}) => {
  const wrapperClasses = classNames("input", {
    [`input--${inputSize}`]: inputSize,
    [`input--${state}`]: state,
    [`input--${inputWidth}-width`]: inputWidth,
  });

  const labelClasses = classNames("input__label", {
    [`background--${labelBackgroundColor}`]:
      labelBackgroundColor && inputSize !== "large",
  });

  return (
    <div className={wrapperClasses} data-align={alignment} title={name}>
      <label htmlFor={name} className={labelClasses}>
        {labelText} {!props?.required && "(optional)"}
      </label>
      <input
        id={name}
        placeholder={placeholderText}
        className="input__field"
        disabled={state === "disabled"}
        type={type}
        {...props}
      />
      {helperText && <p className="input__helper">{helperText}</p>}
      {errorText && (
        <p className="input__helper input__helper--error">{errorText}</p>
      )}
    </div>
  );
};
